import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Loader2, CheckCircle, AlertCircle, Copy, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface Props {
  onEnrolled: () => void;
}

/**
 * MfaSetupScreen — mandatory MFA enrollment for Layer 6/7 users.
 * Shows a TOTP QR code, manual entry key, and verification form.
 */
export default function MfaSetupScreen({ onEnrolled }: Props) {
  const { t } = useTranslation();
  const { profile } = useAuth();

  const [step, setStep] = useState<'enroll' | 'verify' | 'done'>('enroll');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Enrollment state
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  // Verification state
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Step 1: Enroll — generate TOTP factor
  const handleEnroll = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Nizam One — ${profile?.email ?? 'user'}`,
      });

      if (enrollError) throw enrollError;

      setQrUri(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep('verify');
    } catch (err: any) {
      setError(err.message ?? 'Failed to start MFA enrollment');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify — confirm the TOTP code
  const handleVerify = async () => {
    if (!factorId || verifyCode.length !== 6) return;

    setVerifying(true);
    setError(null);
    try {
      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (challengeError) throw challengeError;

      // Verify the challenge
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });
      if (verifyError) throw verifyError;

      setStep('done');
      toast.success(t('mfa.setup_complete', 'MFA enabled successfully!'));

      // Brief delay for the success animation, then proceed
      setTimeout(() => onEnrolled(), 1500);
    } catch (err: any) {
      setError(err.message ?? 'Verification failed. Check the code and try again.');
    } finally {
      setVerifying(false);
    }
  };

  const copySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      toast.success(t('mfa.secret_copied', 'Secret copied to clipboard'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4" dir={profile?.language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            {t('mfa.setup_title', 'Two-Factor Authentication Required')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('mfa.setup_subtitle', 'Your role requires MFA for security. Set up an authenticator app to continue.')}
          </p>
          {/* Arabic */}
          <p className="text-sm text-slate-600 dark:text-slate-500 mt-1">
            {t('mfa.setup_subtitle_ar', 'يتطلب دورك المصادقة الثنائية. قم بإعداد تطبيق المصادقة للمتابعة.')}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
          {/* ── Step 1: Start Enrollment ── */}
          {step === 'enroll' && (
            <div className="space-y-4">
              <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <p>{t('mfa.instructions_1', '1. Install an authenticator app (Google Authenticator, Authy, or Microsoft Authenticator)')}</p>
                <p>{t('mfa.instructions_1_ar', '١. ثبّت تطبيق المصادقة (Google Authenticator أو Authy أو Microsoft Authenticator)')}</p>
                <p>{t('mfa.instructions_2', '2. Click the button below to generate your QR code')}</p>
                <p>{t('mfa.instructions_2_ar', '٢. اضغط على الزر أدناه لإنشاء رمز QR')}</p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                onClick={handleEnroll}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('mfa.generating', 'Generating...')}</>
                ) : (
                  <><Shield className="w-4 h-4" /> {t('mfa.generate_qr', 'Generate QR Code')}</>
                )}
              </button>
            </div>
          )}

          {/* ── Step 2: Scan QR & Verify ── */}
          {step === 'verify' && (
            <div className="space-y-5">
              {/* QR Code */}
              {qrUri && (
                <div className="flex flex-col items-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                    {t('mfa.scan_qr', 'Scan this QR code with your authenticator app:')}
                  </p>
                  <div className="bg-white p-3 rounded-xl">
                    <img src={qrUri} alt="MFA QR Code" className="w-48 h-48" />
                  </div>
                </div>
              )}

              {/* Manual Entry */}
              {secret && (
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500 mb-2">
                    {t('mfa.manual_entry', "Can't scan? Enter this code manually:")}
                  </p>
                  <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2">
                    <code className="flex-1 text-sm text-slate-700 dark:text-slate-200 font-mono tracking-wider">
                      {showSecret ? secret : '•'.repeat(secret.length)}
                    </code>
                    <button onClick={() => setShowSecret(!showSecret)} className="text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white p-1">
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={copySecret} className="text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white p-1">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Verification Input */}
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-2">
                  {t('mfa.enter_code', 'Enter the 6-digit code from your authenticator app:')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-center text-2xl font-mono text-slate-900 dark:text-white tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-600"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && verifyCode.length === 6) handleVerify(); }}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                onClick={handleVerify}
                disabled={verifying || verifyCode.length !== 6}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
              >
                {verifying ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('mfa.verifying', 'Verifying...')}</>
                ) : (
                  <><CheckCircle className="w-4 h-4" /> {t('mfa.verify_enable', 'Verify & Enable MFA')}</>
                )}
              </button>
            </div>
          )}

          {/* ── Step 3: Done ── */}
          {step === 'done' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                {t('mfa.success_title', 'MFA Enabled Successfully!')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('mfa.success_subtitle', 'Your account is now protected with two-factor authentication.')}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-500 mt-1">
                {t('mfa.success_subtitle_ar', 'حسابك محمي الآن بالمصادقة الثنائية.')}
              </p>
              <div className="mt-4">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400 mx-auto" />
                <p className="text-xs text-slate-600 dark:text-slate-500 mt-2">{t('mfa.redirecting', 'Redirecting...')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-700 dark:text-slate-600 mt-4">
          {t('mfa.footer', 'If you have issues setting up MFA, contact IT support.')}
          {' / '}
          {t('mfa.footer_ar', 'إذا واجهت مشاكل، تواصل مع الدعم الفني.')}
        </p>
      </div>
    </div>
  );
}
